<template>
  <SoWidget
    v-bind="$attrs"
    :loading="imbpAvailabilityIsLoading"
    :disabled="meterpoint.supplyStatus === 'NotSupplied'"
  >
    <div class="d-flex align-center mb-3" data-testid="card-title">
      <SoAvatar
        ref="meterpoint-icon"
        :type="meterpoint.isElectricity ? 'electric' : 'gas'"
        :disabled="meterpoint.supplyStatus === 'NotSupplied'"
      />
      <h5 class="ml-2 mr-auto">{{ cardTitle }}</h5>
      <div class="d-flex flex-column">
        <SoTooltip
          v-for="status in meterpointStatuses"
          :key="status.text"
          :text="getStatusTooltipCopy(status)"
          :disabled="!getStatusTooltipCopy(status)"
          location="end"
          max-width="250"
        >
          <SoStatus :status="status" />
        </SoTooltip>
      </div>
    </div>
    <p class="mb-3">
      {{ `${meterpoint.meterpointType}: ${meterpoint.identifier}` }}
    </p>
    <div
      v-for="meter in meterpoint.meters"
      :key="meter.id"
      class="meter-details"
    >
      <p class="mb-3">
        Meter Serial Number:
        {{ meter.identifier || "Unknown" }}
      </p>
      <p
        v-if="meter.isSmartMeter"
        class="mb-3"
        data-testid="last-smart-read-sent"
      >
        <SoTooltip :text="smartReadSentText"> Last smart read sent: </SoTooltip>
        <template v-if="smartMeter?.latestReadingFormatted">
          {{ smartMeter.latestReadingFormatted }}
          {{ getUnit(smartMeter.isElectricity) }}
          <SoTooltip
            v-if="smartMeter?.readingDateTime"
            :text="lastReadTooltipCopy"
            location="end"
            max-width="250"
          >
            {{
              (daysAgoFormatted(smartMeter?.readingDateTime) &&
                `(${daysAgoFormatted(smartMeter?.readingDateTime)})`) ||
              ""
            }}
          </SoTooltip>
        </template>
        <template v-else>None sent</template>
      </p>
      <p
        v-if="meter.isSmartMeter"
        class="mb-3"
        data-testid="last-smart-read-received"
      >
        <SoTooltip :text="smartReadReceivedText">
          Last smart read received:
        </SoTooltip>
        <template v-if="meterpoint.latestSmartRead">
          <SoLink @click="isReadingHistoryModalOpen = true">
            {{ meterpoint.latestSmartRead.cumulative }}
            {{ getUnit(meterpoint.isElectricity) }}
            {{
              daysAgoFormatted(meterpoint.latestSmartRead.date) &&
              `(${daysAgoFormatted(meterpoint.latestSmartRead.date)})`
            }}
            <v-icon icon="chevron-right" />
          </SoLink>
        </template>
        <template v-else>None received</template>
      </p>

      <ReadingHistoryModal
        v-model="isReadingHistoryModalOpen"
        :initial-tab-identifier="meterpoint.identifier"
      />

      <SoButton
        v-if="meterpoint.hasSmartMeter && smartMeter"
        title="See Details"
        icon="chevron-right"
        type="text"
        class="mt-2"
        data-testid="see-details-button"
        @click="showDeviceDetails[meterpoint.identifier] = true"
      />
      <SoButton
        v-if="meterpoint.hasSmartMeter && smartMeter"
        :href="externalRoutes.MeteringTroubleshootingArticles.path"
        title="Troubleshooting"
        icon="external-link"
        type="text"
        class="mt-2"
        target="_blank"
        data-testid="meterpoint-troubleshooting-link"
      />

      <SoDialog
        v-model="showDeviceDetails[meterpoint.identifier]"
        :card-title="cardTitle"
        data-testid="device-details-dialog"
      >
        <SoSimpleTable
          :headers="tableHeaders"
          :items="tableItems(meter)"
          is-pivoted
        />
      </SoDialog>
    </div>

    <SoSkeleton v-if="imbpAvailabilityIsLoading" width="260" height="36" />
    <SoButton
      v-if="canBookSmartMeter"
      :to="{ name: RouteNames.CustomerImbp }"
      title="Book a smart meter installation"
      icon="chevron-right"
      type="text"
      class=""
      data-testid="book-smart-meter-button"
    />
    <SoButton
      v-else-if="hasNoAvailableSlotsForSmartMeter"
      title="No slots - collect availability"
      icon="chevron-right"
      type="text"
      data-testid="smart-meter-interest-button"
      @click="showRegisterInterestModal = true"
    />
  </SoWidget>
  <ImbpRegisterInterestModal v-model="showRegisterInterestModal" />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { RouteNames } from "@/types/router"
  import { IStatus } from "@/types/Status"
  import { ISmartMeter } from "@/models/smartMeter/smartMeter.types"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import { IMeter } from "@/models/meter/meter.types"
  import {
    formatDate,
    formatDateTime,
    daysAgoFormatted,
  } from "@/helpers/dateHelper"
  import { NORMAL_SUPPLY_STATUSES } from "@/constants/meterpoints"
  import SoAvatar from "@/components/SoAvatar.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoLink from "@/components/SoLink.vue"
  import ReadingHistoryModal from "@/views/customer/meterpoints/modals/ReadingHistoryModal.vue"
  import { getUnit } from "@/helpers/meterpointHelper"
  import ImbpRegisterInterestModal from "../../imbp/components/ImbpRegisterInterestModal.vue"
  import { externalRoutes } from "@/constants/routes/externalRoutes"

  export default defineComponent({
    components: {
      SoWidget,
      SoAvatar,
      SoStatus,
      SoButton,
      SoDialog,
      SoSimpleTable,
      SoTooltip,
      SoSkeleton,
      ReadingHistoryModal,
      SoLink,
      ImbpRegisterInterestModal,
    },
    inheritAttrs: false,
    props: {
      meterpoint: {
        type: Object as PropType<IMeterpoint>,
        default: null,
      },
      smartMeter: {
        type: Object as PropType<ISmartMeter | null>,
        default: null,
      },
    },
    data() {
      return {
        current: useCurrentAccountStore(),
        showDeviceDetails: {},
        tableHeaders: [
          "Description",
          "Install date",
          "Installed by",
          "Manufacturer",
          "Model",
          "Meter firmware status",
          "Supply start date",
          "Payment mode",
        ],
        formatDateTime,
        daysAgoFormatted,
        RouteNames,
        isReadingHistoryModalOpen: false,
        getUnit,
        smartReadSentText:
          "Beta - Industry data sourced from ESG Centre Stage about when the smart meter last generated a read. This is used as the basis for the smart meter status",
        smartReadReceivedText:
          "The last smart read that we successfully received, sourced from Junifer",
        showRegisterInterestModal: false,
        externalRoutes,
      }
    },
    computed: {
      cardTitle() {
        const fuelType = this.meterpoint.isElectricity ? "Elec" : "Gas"
        return `${fuelType} ${this.meterTypeLabel} Meter`
      },
      meterTypeLabel(): string {
        if (this.meterpoint.hasUnknownMeter) return "Unknown"
        if (this.meterpoint.hasSmartMeter) return "Smart"
        return "Trad"
      },
      lastReadTooltipCopy() {
        if (!this.smartMeter?.readingDateTime) return
        const readingDateTime = formatDateTime(this.smartMeter.readingDateTime)

        return `The last smart read was sent on ${readingDateTime}.`
      },
      meterpointStatuses() {
        const { hasSmartMeter, dccStatusWithLabel, tradMeterStatus } =
          this.meterpoint
        const workstreamStatuses = this.smartMeter?.workstreamStatuses || []

        if (hasSmartMeter) {
          return workstreamStatuses.length
            ? workstreamStatuses
            : [dccStatusWithLabel]
        }

        const slotStatus = this.current.imbpAvailability.data.slotsStatus

        return this.canBookSmartMeter ? [slotStatus] : [tradMeterStatus]
      },
      isEligibleForSmartMeter() {
        return (
          !this.meterpoint.hasSmartMeter &&
          NORMAL_SUPPLY_STATUSES.includes(this.meterpoint?.supplyStatus)
        )
      },
      canBookSmartMeter() {
        return (
          this.isEligibleForSmartMeter &&
          this.current.imbpAvailability.data.hasAvailableSlots
        )
      },
      imbpAvailabilityIsLoading() {
        return (
          this.isEligibleForSmartMeter &&
          this.current.imbpAvailability.isFetching
        )
      },
      hasNoAvailableSlotsForSmartMeter() {
        return (
          this.isEligibleForSmartMeter &&
          !this.current.imbpAvailability.data.hasAvailableSlots
        )
      },
    },
    methods: {
      tableItems(meter: IMeter) {
        const { meterType: type, meterTypeDescription: description } = meter
        const {
          manufacturerNameFormatted = "",
          modelNameFormatted = "",
          firmwareStatus = "",
          paymentMode = "",
          installer = "",
          ssd,
          installationDate,
        } = this.smartMeter || {}

        return [
          [
            `${type} (${description})`,
            formatDate(installationDate) || "Unknown",
            installer,
            manufacturerNameFormatted,
            modelNameFormatted,
            firmwareStatus,
            formatDate(ssd),
            paymentMode,
          ],
        ]
      },
      getStatusTooltipCopy(status: IStatus) {
        const fuelType = this.meterpoint.isElectricity ? "electricity" : "gas"

        if (this.canBookSmartMeter) {
          return `This customer has a traditional ${fuelType} meter, ask them about upgrading to a smart meter.`
        }

        const statusMapping = {
          Communicating: `This ${fuelType} smart meter has sent us a reading in the last 31 days.`,
          Uncommissioned: `We installed this ${fuelType} smart meter but it has never sent us a reading.`,
          "In-life issue": `This ${fuelType} smart meter previously sent us readings, but has not sent us one in the last 31 days.`,
          Inherited: `We took over this ${fuelType} smart meter from another supplier but it has never sent us a reading.`,
        }

        return statusMapping[status.text] ?? null
      },
    },
  })
</script>

<style lang="css" scoped>
  .meter-details:not(:last-child) {
    margin-bottom: 24px;
  }
</style>
